import React, { FC, useCallback, useEffect, useState } from "react";
import {
    PauseCircleOutlined,
    PlayCircleOutlined,
    SearchOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { Empty } from "antd";
import "../../styles/reset.scss";
import "../manage/manage-all.scss";
import "../manage/system/VoiceManage.scss";
import "./VoicePublic.scss";
import { resolvePublicVoicePreviewFullUrl } from "../../service/voicePublic";
import { getDictDataListSimpleApi } from "../../service/dict";
import { fetchPublicVoiceListMulti } from "./publicVoiceMultiFetch";
import { MineVoicePanel } from "./MineVoicePanel";
import { VIDEO_CDN_PREFIX } from "../../config/video";
import { VoiceOptions } from "../../components/common/VoiceOptions";
import { AlertMessage } from "../../components/common/AlertMessage";
import { useAlert } from "../../hooks/useAlert";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import { getLanguageName } from "../../utils/languageUtils";
import {
    parseVoicePreviewId,
    playVoicePreview,
    stopVoicePreview,
    subscribeVoicePreview,
} from "./voicePreviewPlayer";

const AVATAR_GRADIENTS = [
    "linear-gradient(135deg, #f3f4f6, #e5e7eb)",
    "linear-gradient(135deg, #fce7f3, #fbcfe8)",
    "linear-gradient(135deg, #dbeafe, #bfdbfe)",
    "linear-gradient(135deg, #fef3c7, #fde68a)",
];

const WAVE_PATTERN = [6, 10, 14, 8, 12, 16, 10, 6, 8, 4];

function waveHeightsForId(id: number): number[] {
    const off = id % 7;
    return WAVE_PATTERN.map((_, i) => WAVE_PATTERN[(i + off) % WAVE_PATTERN.length]);
}

export interface VoicePublicProps {
    /** 为 true 时供工作台 `home-series__scroll` 嵌套展示：去掉外层 main-content 与多余边距 */
    embedded?: boolean;
    /** 默认激活的 tab */
    defaultTab?: "public" | "mine";
}

export const VoicePublic: FC<VoicePublicProps> = ({ embedded = false, defaultTab = "public" }) => {
    const { alertConfig, showError, hideAlert } = useAlert();
    const [voiceList, setVoiceList] = useState<VoicePublic.VoiceToneItem[]>([]);
    const [activeTab, setActiveTab] = useState<string>(defaultTab);

    const [langOptions, setLangOptions] = useState<Dict.DictItem[]>([]);
    const [genderOptions, setGenderOptions] = useState<Dict.DictItem[]>([]);
    const [ageOptions, setAgeOptions] = useState<Dict.DictItem[]>([]);
    const [timbreOptions, setTimbreOptions] = useState<Dict.DictItem[]>([]);

    const [selectedLangCodes, setSelectedLangCodes] = useState<string[]>([]);
    const [selectedGenderValues, setSelectedGenderValues] = useState<string[]>([]);
    const [selectedAgeValues, setSelectedAgeValues] = useState<string[]>([]);
    const [selectedTimbreValues, setSelectedTimbreValues] = useState<string[]>([]);
    const [voiceNameInput, setVoiceNameInput] = useState("");
    const [debouncedVoiceName, setDebouncedVoiceName] = useState("");
    const [playingId, setPlayingId] = useState<number | null>(null);
    const CDN_PREFIX = VIDEO_CDN_PREFIX;

    useEffect(() => {
        const t = window.setTimeout(() => {
            setDebouncedVoiceName(voiceNameInput.trim());
        }, 400);
        return () => window.clearTimeout(t);
    }, [voiceNameInput]);

    useEffect(() => {
        return subscribeVoicePreview((key) => {
            setPlayingId(parseVoicePreviewId(key, "public:"));
        });
    }, []);

    useEffect(() => {
        stopVoicePreview();
        return () => stopVoicePreview();
    }, [activeTab]);

    useEffect(() => {
        const fetchDicts = async () => {
            try {
                const [langRes, genderRes, ageRes, timbreRes] = await Promise.all([
                    getDictDataListSimpleApi("target_language"),
                    getDictDataListSimpleApi("system_user_sex"),
                    getDictDataListSimpleApi("age_category"),
                    getDictDataListSimpleApi("voice_type"),
                ]);
                if (langRes.code === 0 && Array.isArray(langRes.data)) setLangOptions(langRes.data);
                if (genderRes.code === 0 && Array.isArray(genderRes.data)) setGenderOptions(genderRes.data);
                if (ageRes.code === 0 && Array.isArray(ageRes.data)) setAgeOptions(ageRes.data);
                if (timbreRes.code === 0 && Array.isArray(timbreRes.data)) setTimbreOptions(timbreRes.data);
            } catch (error) {
                console.error("获取字典数据失败:", error);
            }
        };
        void fetchDicts();
    }, []);

    const isPending = (item: VoicePublic.VoiceToneItem) =>
        item.cloneFinished === false && item.cloneFailed === false;
    const isFailed = (item: VoicePublic.VoiceToneItem) => item.cloneFailed === true;

    const handlePlay = (item: VoicePublic.VoiceToneItem) => {
        if (isPending(item)) {
            showError("音色克隆未完成，暂不可操作");
            return;
        }
        if (isFailed(item)) {
            showError("音色克隆失败，无法试听");
            return;
        }
        const fullUrl = resolvePublicVoicePreviewFullUrl(item, CDN_PREFIX);
        if (!fullUrl) {
            console.warn("暂无合成试听音频", item);
            return;
        }
        playVoicePreview(`public:${item.id}`, fullUrl);
    };

    const reloadPublicVoiceList = useCallback(async () => {
        const list = await fetchPublicVoiceListMulti(
            debouncedVoiceName,
            selectedLangCodes,
            selectedGenderValues,
            selectedAgeValues,
            selectedTimbreValues
        );
        setVoiceList(list);
        return list;
    }, [
        debouncedVoiceName,
        selectedLangCodes,
        selectedGenderValues,
        selectedAgeValues,
        selectedTimbreValues,
    ]);

    useEffect(() => {
        if (activeTab !== "public") {
            return;
        }
        void reloadPublicVoiceList().catch((error) => {
            console.error("获取公共音色列表失败:", error);
            setVoiceList([]);
        });
    }, [activeTab, reloadPublicVoiceList]);

    const hasPendingPublicVoices = voiceList.some((item) => isPending(item));
    useEffect(() => {
        if (activeTab !== "public" || !hasPendingPublicVoices) return undefined;
        const timer = window.setInterval(() => {
            void reloadPublicVoiceList().catch(() => {});
        }, 5000);
        return () => window.clearInterval(timer);
    }, [activeTab, hasPendingPublicVoices, reloadPublicVoiceList]);

    const voicePublicTabs = [
        { label: "公共音色", value: "public" },
        { label: "我的音色", value: "mine" },
    ];

    const inner = (
        <div
            className={["voice-public-container", embedded ? "voice-public-container--embedded" : ""]
                .filter(Boolean)
                .join(" ")}
        >
            {!embedded ? (
                <div className="voice-public-header">
                    <h2>公共音色库</h2>
                </div>
            ) : null}
            <div className="voice-public-wrapper">
                <div className="voice-public-card">
                    <div className="voice-public-card__tabs">
                        <div className="voice-public-card__tabs-left">
                            {voicePublicTabs.map((tab) => (
                                <button
                                    key={tab.value}
                                    type="button"
                                    className={[
                                        "voice-public-tab-item",
                                        tab.value === activeTab ? "voice-public-tab-item--active" : "",
                                    ]
                                        .filter(Boolean)
                                        .join(" ")}
                                    onClick={() => {
                                        if (tab.value !== activeTab) setActiveTab(tab.value);
                                    }}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {activeTab === "public" && (
                        <>
                            <div className="voice-public-card__toolbar">
                                <div className="voice-public-search">
                                    <SearchOutlined className="voice-public-search__icon" />
                                    <input
                                        type="search"
                                        className="voice-public-search__input"
                                        placeholder="搜索音色..."
                                        value={voiceNameInput}
                                        onChange={(e) => setVoiceNameInput(e.target.value)}
                                        aria-label="搜索音色"
                                    />
                                </div>
                                <div className="voice-public-toolbar__spacer" />
                                <VoiceOptions
                                    filterMode="multi"
                                    langValues={selectedLangCodes}
                                    genderValues={selectedGenderValues}
                                    ageValues={selectedAgeValues}
                                    timbreValues={selectedTimbreValues}
                                    onLangValuesChange={setSelectedLangCodes}
                                    onGenderValuesChange={setSelectedGenderValues}
                                    onAgeValuesChange={setSelectedAgeValues}
                                    onTimbreValuesChange={setSelectedTimbreValues}
                                />
                            </div>

                            <div className="voice-public-card__body">
                                <table className="voice-public-matrix">
                                    <thead>
                                        <tr>
                                            <th>音色名称</th>
                                            <th>标签特征</th>
                                            <th className="col-actions">试听 / 操作</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {voiceList.length === 0 ? (
                                            <tr className="voice-public-matrix__empty-row">
                                                <td colSpan={3}>
                                                    <div className="voice-public-empty" role="status">
                                                        <Empty
                                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                            description={
                                                                <div className="voice-public-empty__title">
                                                                    暂无公共音色
                                                                </div>
                                                            }
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            voiceList.map((item, index) => {
                                                const langName =
                                                    item.soundTextValue || getLanguageName(item.langCode || "") || item.langCode || "-";
                                                const showLang = langName && langName !== "-";
                                                const hasAge = !!item.ageTextValue?.trim();
                                                const hasGender = !!item.genderTextValue?.trim();
                                                const hasTimbre = !!item.timbreTextValue?.trim();
                                                const grad = AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length];
                                                const waves = waveHeightsForId(item.id);
                                                return (
                                                    <tr key={item.id}>
                                                        <td>
                                                            <div className="voice-public-voice-card">
                                                                <div
                                                                    className="voice-public-avatar"
                                                                    style={{ background: grad }}
                                                                    aria-hidden
                                                                >
                                                                    <UserOutlined />
                                                                </div>
                                                                <div className="voice-public-voice-info">
                                                                    <div
                                                                        className="voice-public-voice-name"
                                                                        title={item.voiceName}
                                                                    >
                                                                        <span className="voice-public-voice-name-text">{item.voiceName}</span>
                                                                        {isPending(item) ? (
                                                                            <span className="voice-public-pending-badge" role="status">
                                                                                <span className="voice-public-pending-spinner" aria-hidden />
                                                                                处理中
                                                                            </span>
                                                                        ) : null}
                                                                        {isFailed(item) ? (
                                                                            <span className="voice-public-failed-badge">失败</span>
                                                                        ) : null}
                                                                    </div>
                                                                    {item.description?.trim() ? (
                                                                        <div className="voice-public-voice-description" title={item.description}>
                                                                            {item.description}
                                                                        </div>
                                                                    ) : null}
                                                                    <div
                                                                        className={[
                                                                            "voice-public-wave",
                                                                            playingId === item.id ? "voice-public-wave--playing" : "",
                                                                        ]
                                                                            .filter(Boolean)
                                                                            .join(" ")}
                                                                        style={
                                                                            {
                                                                                "--vp-wave-duration": `${0.95 + (item.id % 5) * 0.12}s`,
                                                                                "--vp-wave-delay-base": `${(item.id % 7) * 0.03}s`,
                                                                            } as React.CSSProperties
                                                                        }
                                                                        aria-hidden
                                                                    >
                                                                        {waves.map((h, wi) => (
                                                                            <div
                                                                                key={wi}
                                                                                className="voice-public-wave-bar"
                                                                                style={{ height: h }}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="voice-public-tag-wrap">
                                                                {showLang && (
                                                                    <span className="voice-public-tag voice-public-tag--lang">
                                                                        {langName}
                                                                    </span>
                                                                )}
                                                                {hasAge && (
                                                                    <span className="voice-public-tag voice-public-tag--age">
                                                                        {item.ageTextValue}
                                                                    </span>
                                                                )}
                                                                {hasGender && (
                                                                    <span className="voice-public-tag voice-public-tag--gender">
                                                                        {item.genderTextValue}
                                                                    </span>
                                                                )}
                                                                {hasTimbre && (
                                                                    <span className="voice-public-tag voice-public-tag--category">
                                                                        {item.timbreTextValue}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="col-actions">
                                                            <div className="voice-public-action-group">
                                                                <button
                                                                    type="button"
                                                                    className={[
                                                                        "action-btn",
                                                                        "play-btn",
                                                                        playingId === item.id ? "play-btn--playing" : "",
                                                                    ]
                                                                        .filter(Boolean)
                                                                        .join(" ")}
                                                                    title={
                                                                        isPending(item)
                                                                            ? "处理中，暂不可试听"
                                                                            : isFailed(item)
                                                                              ? "克隆失败，无法试听"
                                                                              : playingId === item.id
                                                                                ? "暂停"
                                                                                : "试听"
                                                                    }
                                                                    disabled={isPending(item) || isFailed(item)}
                                                                    onClick={() => handlePlay(item)}
                                                                >
                                                                    {playingId === item.id ? (
                                                                        <PauseCircleOutlined />
                                                                    ) : (
                                                                        <PlayCircleOutlined />
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {activeTab === "mine" && (
                        <MineVoicePanel />
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <AlertMessage config={alertConfig} onClose={hideAlert} zIndex={ALERT_Z_INDEX.UPLOAD_VIDEO} />
            {embedded ? inner : <div className="main-content">{inner}</div>}
        </>
    );
};
